import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useAuth from '../../../../../hooks/useAuth';
import { cancelSubscription, getMembership } from '../../../../../services/designer';
import { IMembership } from '../../../../../types/accountSettings.types';
import { getDate } from '../../../../../utils/functions';
import Skeleton from '../../../../atoms/Skeleton';

const PayPalManageUrl = 'https://www.paypal.com/myaccount/autopay/';

const Membership = () => {
  const {
    subscriptionStatus: { subscriptionActive, subscriptionId, nextBillingTime },
    checkAuth
  } = useAuth();

  const [membershipInfo, setMembershipInfo] = useState<IMembership>();
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const memberExpireDate = useMemo(() => {
    const raw = membershipInfo?.member_expire ?? '';
    const d = raw ? new Date(raw) : undefined;
    return d && !isNaN(d.getTime()) ? d : undefined;
  }, [membershipInfo]);

  const isExpired = useMemo(() => {
    if (!memberExpireDate) return false;
    const endOfDay = new Date(memberExpireDate);
    endOfDay.setHours(23, 59, 59, 999);
    return endOfDay < new Date();
  }, [memberExpireDate]);

  const mismatchActiveButExpired = subscriptionActive && isExpired;

  const getMessage = () => {
    if (subscriptionActive && subscriptionId) {
      return (
        <>
          Your membership is set to automatically renew.
          <br />
          Your next automatic payment is scheduled for{' '}
          <strong>{getDate(nextBillingTime || '', 'long')}</strong>.
        </>
      );
    } else {
      return (
        <>
          Your membership is not set to automatically renew. Your membership{' '}
          {memberExpireDate ? (
            <>
              expires on{' '}
              <strong>{getDate(memberExpireDate.toISOString(), 'long')}</strong>.
            </>
          ) : (
            <>expiration date is currently unavailable.</>
          )}
        </>
      );
    }
  };

  const handleCancel = async () => {
    if (!subscriptionId) return;
    setBusy(true);
    try {
      await cancelSubscription(subscriptionId as number);
      await checkAuth();
      navigate('/checkout');
    } catch (error) {
      console.error('Error canceling subscription:', error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    getMembership()
      .then(data => {
        if (data) setMembershipInfo(data);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <section>
      <div className="content_outer">
        {loading ? (
          <Skeleton width={'100%'} height={20} />
        ) : (
          <div id="membership">
            {mismatchActiveButExpired ? (
              <>
                <p>
                  <strong>
                    We noticed a mismatch between your PayPal subscription and your
                    LogoLounge membership.
                  </strong>
                </p>
                <p>Here’s how to fix it:</p>
                <p>
                  1. Log into PayPal and check that your most recent payment went through.
                  <br />
                  2. If it did, but your LogoLounge account still shows expired, email us
                  at{' '}
                  <a
                    href={`mailto:info@logolounge.com?subject=Membership%20not%20updated%20but%20PayPal%20active&body=SubscriptionID:%20${subscriptionId ?? ''}`}
                  >
                    info@logolounge.com
                  </a>
                  .
                  <br />
                  Be sure to include your Subscription ID
                  {subscriptionId ? `: ${subscriptionId}` : '.'}
                </p>
                <p>
                  We’ll get it sorted quickly so you can get back to sharing your work.
                </p>
                <p>
                  As a last resort, you can cancel your subscription here and then
                  re-subscribe.
                </p>

                <div
                  className="button_outer"
                  style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                >
                  <a
                    className="renew_button"
                    href={PayPalManageUrl}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Manage in PayPal
                  </a>
                  <button className="renew_button" onClick={handleCancel} disabled={busy}>
                    {busy ? 'Cancelling…' : 'Cancel Subscription'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <p>{getMessage()}</p>

                <div
                  className="button_outer"
                  style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
                >
                  {subscriptionActive && subscriptionId ? (
                    <>
                      <a
                        className="renew_button"
                        href={PayPalManageUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Review Subscription in PayPal
                      </a>
                      <button
                        className="renew_button"
                        onClick={handleCancel}
                        disabled={busy}
                      >
                        {busy ? 'Cancelling…' : 'Cancel Subscription'}
                      </button>
                    </>
                  ) : (
                    <Link className="renew_button" to="/checkout">
                      Subscribe
                    </Link>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default Membership;
